/**
 * VoiceConfigEditor - Reusable voice configuration editor
 *
 * This component is shared across all widget types. It lets users
 * configure how the voice agent interacts with a widget:
 * - Speakable: Can the agent describe this widget?
 * - Trigger Phrases: What words activate this widget?
 * - Description: Extra context for the agent about this widget
 *
 * Appears as an expandable "Voice Settings" section when a widget is selected.
 */

import { useState, useEffect } from 'react';
import type { WidgetVoiceConfig } from '@deep-agents/shared-types';

interface VoiceConfigEditorProps {
  /** Current voice config (may be undefined for new widgets) */
  config?: WidgetVoiceConfig;
  /** Called whenever a voice setting changes */
  onChange: (config: WidgetVoiceConfig) => void;
}

export function VoiceConfigEditor({ config, onChange }: VoiceConfigEditorProps) {
  // Track whether the panel is expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(false);

  // Local state for each field so we can edit without firing onChange on every keystroke
  const [speakable, setSpeakable] = useState(config?.speakable ?? true);
  const [triggerPhrases, setTriggerPhrases] = useState(
    config?.triggerPhrases?.join(', ') || ''
  );
  const [description, setDescription] = useState(config?.description || '');

  // Sync local state if parent config changes (e.g., undo/redo)
  useEffect(() => {
    setSpeakable(config?.speakable ?? true);
    setTriggerPhrases(config?.triggerPhrases?.join(', ') || '');
    setDescription(config?.description || '');
  }, [config]);

  // Helper: build updated config and notify parent
  const emitChange = (overrides: Partial<WidgetVoiceConfig>) => {
    onChange({
      speakable,
      triggerPhrases: triggerPhrases
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
      description: description || undefined,
      ...overrides,
    });
  };

  return (
    <div
      className="mt-2 border-t border-gray-100"
      // Stop clicks from propagating to the widget's selection handler
      onClick={(e) => e.stopPropagation()}
    >
      {/* Toggle button to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:text-violet-600 transition-colors"
      >
        <span>{isExpanded ? '▼' : '▶'}</span>
        <span>Voice Settings</span>
        {/* Show a quick indicator if voice is configured */}
        {config?.triggerPhrases && config.triggerPhrases.length > 0 && (
          <span className="ml-auto text-[10px] text-violet-500">
            {config.triggerPhrases.length} trigger{config.triggerPhrases.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {/* Expanded editor panel */}
      {isExpanded && (
        <div className="px-2 pb-2 space-y-2">
          {/* Speakable checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={speakable}
              onChange={(e) => {
                setSpeakable(e.target.checked);
                emitChange({ speakable: e.target.checked });
              }}
              className="nodrag nopan rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            <span className="text-xs text-gray-600">Agent can describe this</span>
          </label>

          {/* Trigger phrases input */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
              Trigger Phrases (comma-separated)
            </label>
            <input
              type="text"
              value={triggerPhrases}
              onChange={(e) => setTriggerPhrases(e.target.value)}
              onBlur={() => {
                // Save when user leaves the field
                emitChange({
                  triggerPhrases: triggerPhrases
                    .split(',')
                    .map((p) => p.trim())
                    .filter(Boolean),
                });
              }}
              placeholder="e.g. show products, view menu"
              className="nodrag nopan w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white text-gray-700"
            />
          </div>

          {/* Description textarea */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
              Description for Agent
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => {
                emitChange({ description: description || undefined });
              }}
              placeholder="What should the agent know about this widget?"
              rows={2}
              className="nodrag nopan w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white text-gray-700 resize-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
